import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ArgusAnimal, Department, Firm, HexAdministration, HexDiagnosis, HexServingMode, Prescription, Species, TreatmentInstruction, ValidationError } from 'src/app/api/models';
import { AppState } from 'src/app/store/states/app.state';
import { Store, select } from '@ngrx/store';
import * as fromStaticDataSelectors from 'src/app/store/selectors/static-data.selector';
import * as fromStaticDataReasonActions from 'src/app/store/actions/static-data.action';
import { componentDestroyed, untilComponentDestroyed } from 'src/app/shared/helpers/rxjs.operator';
import { SpeciesDiagnosisDropdown } from 'src/app/medical-history/models/species-diagnosis-dropdown.model';
import { Languages } from 'src/app/shared/translation/languages.enum';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { PrescriptionHexRow } from 'src/app/medical-history/models/prescription-hex-row';
import { VisitTreatmentHex } from 'src/app/medical-history/models/visit-treatment-hex.model';
import { CustomerService, MedicalHistoryService, PrescriptionsService } from 'src/app/api/services';
import { SavePrescriptions$Params } from 'src/app/api/fn/medical-history/save-prescriptions';
import { ToastrMsgService } from 'src/app/shared/services/toastr-msg.service';
import { CustomTranslationService } from 'src/app/shared/translation/custom-translation.service';
import { MedicalHistoryHelper } from 'src/app/shared/helpers/medical-history.helper';
import { DateTimeHelper } from 'src/app/shared/helpers/datetime.helper';
import { PrescriptionStatus } from 'src/app/medical-history/enums/prescription-status.enum';
import { HelperService } from 'src/app/shared/services/helper.service';
import { GuidHelper } from 'src/app/shared/helpers/guid.helper';
import { EventGeneratorService } from 'src/app/calendar/shared/services/event-generator.service';
import { PrescriptionWithId } from 'src/app/medical-history/models/prescription-with-id.model';
import { PrescriptionModalParams } from 'src/app/medical-history/shared/models/prescription-modal-params.model';
import { DeletePrescription$Params } from 'src/app/api/fn/prescriptions/delete-prescription';
import { debounceTime, Observable, of, startWith } from 'rxjs';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ErrorMsgHelper } from 'src/app/shared/helpers/errormsg.helper';
import { DEBOUNCE_TIME_INPUT } from 'src/app/shared/helpers/constants';
import { PrescriptionType } from 'src/app/medical-history/enums/prescription-type.enum';
import { AnimalDetails } from 'src/app/medical-history/models/animal-details.model';
import { AnimalHelper } from 'src/app/shared/helpers/animal.helper';
import { DeliveryOnStockTotalQuantityValidator } from 'src/app/shared/validators/delivery-on-stock-total-quantity.validator';
import { PrescriptionHelper } from 'src/app/shared/helpers/prescription.helper';
import { RgTextList } from 'src/app/medical-history/models/rg-text-list.model';

enum PrescriptionDataPages {
  calendarPicker = 'calendarPicker',
  prescriptionData = 'prescriptionData',
}
enum PrescriptionSource {
  ISABV,
  TAPS,
  UNKNOWN
}

interface TranslationError {
  key?: string,
  de?: string,
  fr?: string,
  it?: string,
  tapsErrorMsg?: string,
  source: PrescriptionSource
}

interface ErrorHex {
  treatmentName?: string,
  messages?: Array<TranslationError>
}

@Component({
  selector: 'diana-hex-prescription-modal',
  templateUrl: './hex-prescription-modal.component.html',
  styleUrls: ['./hex-prescription-modal.component.scss'],
  standalone: false
})
export class HexPrescriptionModalComponent implements OnDestroy, OnInit {
  @ViewChild(MatAutocompleteTrigger) autoCompleteTrigger!: MatAutocompleteTrigger; // Get trigger reference
  public showPrescriptionValidation = false;
  public isLoadedSpecisDiagnosis = false;
  public formGroup!: FormGroup;
  public animalSpeciesList: Array<Species> = [];
  public diagnosisList: SpeciesDiagnosisDropdown[] = [];
  public currentPage: PrescriptionDataPages = PrescriptionDataPages.prescriptionData;
  public pages = PrescriptionDataPages;
  public firm!: Firm;
  public currentPrescriptionDiagnosis!: Array<HexDiagnosis>;
  public selectedDepartment?: Department;
  public currentLanguage = Languages.de;
  public Languages = Languages;
  public treatmentPrescriptions: PrescriptionHexRow[] = [];
  public showConfirmation = false;
  public servingModes: Array<HexServingMode> | undefined = [];
  public applicationTypes: Array<HexAdministration> = [];
  public errorResponse: Array<ErrorHex> = [];
  public PrescriptionStatus = PrescriptionStatus;
  public isOnlyDeliveryOnStock = true;
  public datePrescription!: FormControl;
  protected readonly PrescriptionSource = PrescriptionSource;

  get visitAtControl(): FormControl {
    return this.formGroup.get('date') as FormControl;
  }
  get timeControl(): FormControl {
    return this.formGroup.get('time') as FormControl;
  }
  get animalSpeciesControl(): FormControl {
    return this.formGroup.get('animalSpecies') as FormControl;
  }
  get weightControl(): FormControl {
    return this.formGroup.get('weight') as FormControl;
  }
  get hexDiagnosisControl(): FormArray {
    return this.formGroup.get('hexDiagnosis') as FormArray;
  }
  get animalTvdNumberControl(): FormControl {
    return this.formGroup.get('animalTvdNumber') as FormControl;
  }

  searchControl = new FormControl('');
  options: ArgusAnimal[] = []; // Store API data
  filteredOptions!: Observable<ArgusAnimal[]>;
  isArgusAnimalLoaded = false; // Flag to track first time load
  showLoading = false; // Flag to track visibility of the spinner in the argus animal fropdown
  public tvdNumberLoadingError = '';
  constructor(
    public dialogRef: MatDialogRef<HexPrescriptionModalComponent>,
    private store$: Store<AppState>,
    private localStorageService: LocalStorageService,
    private fb: FormBuilder,
    private toastrMsg: ToastrMsgService,
    private medicalHistoryService: MedicalHistoryService,
    private translateSrv: CustomTranslationService,
    private helperService: HelperService,
    private changeDetector: ChangeDetectorRef,
    private eventGenerator: EventGeneratorService,
    private prescriptionService: PrescriptionsService,
    private translationService: CustomTranslationService,
    private customerService: CustomerService,
    @Inject(MAT_DIALOG_DATA) public params: PrescriptionModalParams,
  ) {
    this.store$.dispatch(fromStaticDataReasonActions.initHexData());
    this.formGroup = this.fb.group({
      date: [DateTimeHelper.copyDate(this.params.visitAt), Validators.required],
      time: [DateTimeHelper.copyDate(this.params.visitAt), Validators.required],
      animalSpecies: ['', Validators.required],
      animalTvdNumber: [''],
      weight: [this.params.weight || undefined],
      hexDiagnosis: this.fb.array([])
    });

    if (this.params.firms && this.params.firms.length > 0) {
      this.params.firms.map(firm => {
        const firmDepartment = firm.departments?.find(department => department.departmentId === this.params.selectedDepartmentId)
        if (firmDepartment) {
          this.selectedDepartment = firmDepartment;
          this.firm = firm;
        }
      });
    }
    this.loadAnimalSpecies();
    this.currentLanguage = this.localStorageService.getLanguage() || Languages.de;
    this.showPrescriptionValidation = this.params.openFromPrescriptionReport;
    if (this.showPrescriptionValidation) {
      this.loadDiagnosisSpecies();
    }
  }

  ngOnInit(): void {
    this.animalTvdNumberControl.valueChanges.pipe(
      startWith(''),
      debounceTime(DEBOUNCE_TIME_INPUT),
    ).subscribe(value => {
      this.filteredOptions = this.filterOptions(value)
    });
  }

  onFocus() {
    if (!this.isArgusAnimalLoaded) {
      this.showLoading = true
      this.customerService.getArgusAnimals({ id: this.params.customerId || '' }).pipe(untilComponentDestroyed(this))
        .subscribe(data => {
          this.options = data;
          this.filteredOptions = of(data);
          this.isArgusAnimalLoaded = true;
          this.showLoading = false;
        }, error => {
          this.isArgusAnimalLoaded = true;
          this.showLoading = false;
          this.filteredOptions = of([]);
          this.tvdNumberLoadingError = ErrorMsgHelper.handleError(this.translateSrv, error) || '';
          this.animalTvdNumberControl.disable();
        });
    }
  }

  private filterOptions(value: string | null): Observable<ArgusAnimal[]> {
    if (typeof (value) === 'string') {
      const filterValue = value?.toLowerCase();
      return of(this.options.filter(option => option?.name?.toLowerCase().includes(filterValue || '') || option?.tvdNumber?.toLowerCase().includes(filterValue || '')));
    }
    return of(this.options);
  }

  displayFn(option: ArgusAnimal | string): string {
    // if its type string it means its a error message of the getArgusAnimals request
    if (typeof (option) === 'string') {
      return option;
    } else {
      return option ? `${option.tvdNumber || ''} ${option.name || ''}` : '';
    }
  }

  ngOnDestroy(): void {
    componentDestroyed(this);
  }

  /**
   * Gets species list and loads the existing precription data if exists
   */
  private loadDiagnosisSpecies(): void {
    this.isLoadedSpecisDiagnosis = false;
    this.store$
      .pipe(
        select(fromStaticDataSelectors.getSpeciesDiagnoses()),
        untilComponentDestroyed(this)
      )
      .subscribe((speciesDiagnosis: SpeciesDiagnosisDropdown[]) => {
        this.diagnosisList = speciesDiagnosis;
        this.isLoadedSpecisDiagnosis = true;
        this.loadAdministrationServingModes();
      });
  }

  private loadAdministrationServingModes(): void {
    this.store$
      .pipe(
        select(fromStaticDataSelectors.getApplicationTypeHexData),
        untilComponentDestroyed(this)
      )
      .subscribe((applicationTypes: HexAdministration[]) => {
        this.applicationTypes = applicationTypes;
        this.store$
          .pipe(
            select(fromStaticDataSelectors.getServingModesHexDataEn),
            untilComponentDestroyed(this)
          )
          .subscribe((servingModes: HexServingMode[] | undefined) => {
            this.servingModes = servingModes;
            this.initializePrescriptionTreatment();
          });
      });
  }

  private disableGeneralFormData(): void {
    this.visitAtControl.disable();
    this.timeControl.disable();
    this.animalSpeciesControl.disable();
    this.weightControl.disable();
    this.hexDiagnosisControl.disable();
  }

  private getValidAnimalSpeciesKey(existingTreatmentPrescription: Prescription | undefined, animalDetails: AnimalDetails): string | undefined {
    let speciesCode = '';
    if (animalDetails.isabvCategory) {
      speciesCode = animalDetails.isabvCategory;
    }
    let totalCodeLevels = 0;
    if (existingTreatmentPrescription) {
      if (existingTreatmentPrescription.animalSpeciesCategoryKey) {
        totalCodeLevels = existingTreatmentPrescription.animalSpeciesCategoryKey.split('.').length;
        speciesCode = totalCodeLevels > 0 ? existingTreatmentPrescription.animalSpeciesCategoryKey : '';
      }
      // if total level is 2 or lower that means that the category on animalSpeciesKey doesnt exist or is 2.1
      if (existingTreatmentPrescription.animalSpeciesCategoryKey && totalCodeLevels <= 2) {
        totalCodeLevels = existingTreatmentPrescription.animalSpeciesCategoryKey.split('.').length;
        speciesCode = totalCodeLevels > 2 ? existingTreatmentPrescription.animalSpeciesCategoryKey : '';
      }
    }

    return speciesCode;
  }

  private initializePrescriptionTreatment(): void {
    if (this.hexDiagnosisControl && this.hexDiagnosisControl.length === 0) {
      this.currentPrescriptionDiagnosis = [];
      this.treatmentPrescriptions = [];
      // loading the selected diagnosis of the visit, if it doesnt exist a saved prescription
      if (this.params.currentTreatmentPrescription.length > 0) {
        this.params.currentTreatmentPrescription.map(data => {
          const treatmentDiagnosis = this.diagnosisList.find(diagnosis => (diagnosis.codeLevel1 + '.' + diagnosis.codeLevel2) === data.diagnosisKey);
          if (treatmentDiagnosis && treatmentDiagnosis.id && !this.currentPrescriptionDiagnosis.find(diagnosis => diagnosis.id === treatmentDiagnosis.id)) {
            this.currentPrescriptionDiagnosis.push({ id: treatmentDiagnosis.id });
          }
        })
      } else {
        if (this.params.currentVisitDiagnosis && this.params.currentVisitDiagnosis.length > 0) {
          this.currentPrescriptionDiagnosis = this.params.currentVisitDiagnosis.filter(diagnosis => diagnosis.diagnosisLevel2).map(values => {
            return {
              id: values.diagnosisLevel2 || '',
            };
          });
        }
      }
      let isEveryPrescriptionSent = false;
      // Generating the treatments table object
      this.params.treatments.map((treatment: VisitTreatmentHex) => {
        const existingTreatmentPrescription = this.params.currentTreatmentPrescription.find(savedTreatmentPrescription => savedTreatmentPrescription.treatmentId === treatment.id);
        const isabvStateTreatment = this.setState(existingTreatmentPrescription, existingTreatmentPrescription?.isabvState as PrescriptionStatus);
        const tapsStateTreatment = this.setState(existingTreatmentPrescription, existingTreatmentPrescription?.tapsState as PrescriptionStatus);
        // setting delivery on stock control variable
        if (!existingTreatmentPrescription) {
          this.isOnlyDeliveryOnStock = (this.isOnlyDeliveryOnStock && (treatment.type === PrescriptionType.SUPPLY_FARM_ANIMAL_TREATMENT ? true : false));
        } else {
          this.isOnlyDeliveryOnStock = (this.isOnlyDeliveryOnStock && (existingTreatmentPrescription.type === PrescriptionType.SUPPLY_FARM_ANIMAL_TREATMENT ? true : false))
        }
        if (existingTreatmentPrescription && existingTreatmentPrescription?.animalWeight) {
          this.weightControl.setValue(existingTreatmentPrescription.animalWeight);
        } else {
          this.weightControl.setValue('');
        }
        this.weightControl.markAsPristine();

        const speciesCodeFromPrescription = this.getValidAnimalSpeciesKey(existingTreatmentPrescription, this.params.animal);
        const currentSpeciesCode = speciesCodeFromPrescription ? speciesCodeFromPrescription : this.params.animal.isabvCategory
        if (currentSpeciesCode && currentSpeciesCode?.length === 5) {
          const species = this.animalSpeciesList.find(species => currentSpeciesCode === (species.codeLevel1 + '.' + species.codeLevel2 + (species.codeLevel3 ? '.' + species.codeLevel3 : '')));
          this.animalSpeciesControl.setValue(species ? species : undefined);
          this.animalSpeciesControl.markAsPristine();
        }
        if (existingTreatmentPrescription && existingTreatmentPrescription?.treatmentStart) {
          this.visitAtControl.setValue(DateTimeHelper.copyDate(existingTreatmentPrescription?.treatmentStart));
          this.visitAtControl.markAsPristine();
          this.timeControl.setValue(DateTimeHelper.copyDate(existingTreatmentPrescription?.treatmentStart));
          this.timeControl.markAsPristine();
        }
        let selectedTreatmentDiagnosisId = '';
        if (existingTreatmentPrescription) {
          selectedTreatmentDiagnosisId = this.diagnosisList.find(diagnosis => (diagnosis.codeLevel1 + '.' + diagnosis.codeLevel2 + (diagnosis.codeLevel3 ? '.' + diagnosis.codeLevel3 : '')) === existingTreatmentPrescription.diagnosisKey)?.diagnosisId || '';
        }
        if (isabvStateTreatment === PrescriptionStatus.SENT || tapsStateTreatment === PrescriptionStatus.SENT ||
          isabvStateTreatment === PrescriptionStatus.RESENT || tapsStateTreatment === PrescriptionStatus.RESENT
        ) {
          isEveryPrescriptionSent = true;
        }

        let isIsabv = false;
        let isTaps = false;
        if (treatment.isTransferISABV === true) {
          isIsabv = this.getHexSelection(treatment.isIsabv || false, existingTreatmentPrescription ? isabvStateTreatment : treatment.isabvPrescriptionStatus || undefined);
        }

        if (treatment.isTransferTAPS === true) {
          isTaps = this.getHexSelection(treatment.isTaps || false, existingTreatmentPrescription ? tapsStateTreatment : treatment.tapsPrescriptionStatus || undefined);
        }

        // Disables all form form inputs
        if (isEveryPrescriptionSent) {
          this.disableGeneralFormData();
        }
        this.store$
          .pipe(
            select(fromStaticDataSelectors.getServiceArticleTreatmentsByArticleId(treatment.articleId)),
            untilComponentDestroyed(this)
          )
          .subscribe(serviceArticleTreatmentsByArticleId => {
            const treatmentDefaultData = serviceArticleTreatmentsByArticleId.find(serviceArticleTreatment => serviceArticleTreatment.salesArticle?.id === treatment.salesArticleId);
            const ratioTreatmentValue = MedicalHistoryHelper.convertHexQuantity(treatment.quantity || 0, treatment.salesArticleId || '', treatment.articleId || '', serviceArticleTreatmentsByArticleId);
            const isResendingCancelledPrescriptions = this.checkIsResendingCancelledPrescriptions();
            const dosageTreatmentInstruction = existingTreatmentPrescription ? {
              injectionPointWithdrawalPeriod: existingTreatmentPrescription.injectionPointWithdrawalPeriod,
              meatWithdrawalPeriod: existingTreatmentPrescription.meatWithdrawalPeriod,
              organWithdrawalPeriod: existingTreatmentPrescription.organWithdrawalPeriod,
              milkWithdrawalPeriod: existingTreatmentPrescription.milkWithdrawalPeriod,
              administrationId: this.getAdministrationId(existingTreatmentPrescription.medicineAdministration),
              firstDosage: existingTreatmentPrescription?.firstDosis,
              firstDuration: existingTreatmentPrescription.firstTreatmentDays,
              firstServingModeId: this.getServingModeId(existingTreatmentPrescription.firstServingMode),
              secondDosage: existingTreatmentPrescription.secondDosis,
              secondDuration: existingTreatmentPrescription.secondTreatmentDays,
              secondServingModeId: this.getServingModeId(existingTreatmentPrescription.secondServingMode),
              minMilkWithdrawalPeriod: existingTreatmentPrescription.minMilkWithdrawalPeriod,
              minMeatWithdrawalPeriod: existingTreatmentPrescription.minMeatWithdrawalPeriod,
              minOrganWithdrawalPeriod: existingTreatmentPrescription.minOrganWithdrawalPeriod,
              minInjectionPointWithdrawalPeriod: existingTreatmentPrescription.minInjectionPointWithdrawalPeriod
            } : this.convertTreatmentInstructionsToHexUnits(treatment.treatmentInstruction, treatment.salesArticleId || '', treatment.articleId || '', serviceArticleTreatmentsByArticleId);
            let currentTreatmentPrescription: PrescriptionHexRow = {
              treatmentId: existingTreatmentPrescription?.treatmentId || treatment.id,
              articleId: treatment.articleId || '',
              salesArticleId: treatment.salesArticleId || '',
              medicineDescription: treatment.description,
              tapsState: this.setState(existingTreatmentPrescription, tapsStateTreatment as PrescriptionStatus),
              isabvState: this.setState(existingTreatmentPrescription, isabvStateTreatment as PrescriptionStatus),
              totalDosis: existingTreatmentPrescription?.totalDosis ? ((treatment.quantity || 0) * existingTreatmentPrescription?.totalDosis) / (ratioTreatmentValue) : 0,
              totalDeliveryQuantity: treatment.quantity,
              treatmentQuantity: treatment.quantity,
              hexUnit: treatmentDefaultData?.salesArticle?.hexUnitCode || '',
              hexTotalDeliveryQuantity: ratioTreatmentValue,
              hexTotalDosis: existingTreatmentPrescription?.totalDosis ? existingTreatmentPrescription?.totalDosis : 0,
              treatmentInstruction: dosageTreatmentInstruction,
              unit: treatment.unit,
              startDateInstructions: existingTreatmentPrescription && existingTreatmentPrescription.treatmentStart ? new FormControl(DateTimeHelper.copyDate(existingTreatmentPrescription.treatmentStart)) : new FormControl(DateTimeHelper.copyDate(treatment.treatmentInstruction?.startAt || this.params.visitAt)),
              endDateInstructions: existingTreatmentPrescription && existingTreatmentPrescription.treatmentEnd ? new FormControl(DateTimeHelper.copyDate(existingTreatmentPrescription.treatmentEnd)) : new FormControl(DateTimeHelper.copyDate(treatment.treatmentInstruction?.endAt || this.params.visitAt)),
              isTransferISABV: treatment.isTransferISABV,
              isTransferTAPS: treatment.isTransferTAPS,
              isIsabv: new FormControl(isIsabv),
              isTaps: new FormControl(isTaps),
              diagnosisId: selectedTreatmentDiagnosisId,
              prescriptionNumber: isResendingCancelledPrescriptions ? GuidHelper.newGuid() : existingTreatmentPrescription?.prescriptionNumber || GuidHelper.newGuid(),
              hexDescription: treatment.hexDescription,
              expanded: this.params.treatments.length === 1 ? true : false, // To show expanded when there is only one treatment
              isCriticalAntibiotic: treatmentDefaultData?.salesArticle?.isCriticalAntibiotic,
              serviceArticleTreatmentsByArticleId: serviceArticleTreatmentsByArticleId,
              openFromPrescriptionReport: this.params.openFromPrescriptionReport,
              type: existingTreatmentPrescription ? new FormControl(existingTreatmentPrescription?.type) : new FormControl(treatment.type),
              dosagesFormGroup: this.fb.group({
                dosages: this.fb.array([]),
                milkWithdrawalPeriod: [dosageTreatmentInstruction?.milkWithdrawalPeriod !== undefined ? dosageTreatmentInstruction?.milkWithdrawalPeriod : ''],
                meatWithdrawalPeriod: [dosageTreatmentInstruction?.meatWithdrawalPeriod !== undefined ? dosageTreatmentInstruction?.meatWithdrawalPeriod : ''],
                organWithdrawalPeriod: [dosageTreatmentInstruction?.organWithdrawalPeriod !== undefined ? dosageTreatmentInstruction?.organWithdrawalPeriod : ''],
                injectionPointWithdrawalPeriod: [dosageTreatmentInstruction?.injectionPointWithdrawalPeriod !== undefined ? dosageTreatmentInstruction?.injectionPointWithdrawalPeriod : ''],
              }),
              deliveryOnStockDosageFormGroup: this.fb.group({
                treatingAnimalsNumber: [existingTreatmentPrescription?.animalCount || '', Validators.required],
                treatmentDays: [existingTreatmentPrescription?.firstTreatmentDays || ''],
                animalDosis: [existingTreatmentPrescription?.firstDosis || ''],
                units: [{ value: treatmentDefaultData?.salesArticle?.hexUnitCode || '', disabled: true }],
              }, {
                validators: DeliveryOnStockTotalQuantityValidator(
                  'treatingAnimalsNumber',
                  'treatmentDays',
                  'animalDosis',
                  ratioTreatmentValue,
                )
              })
            }
            if (currentTreatmentPrescription.type.value === PrescriptionType.SINGLE_FARM_ANIMAL_TREATMENT) {
              currentTreatmentPrescription.deliveryOnStockDosageFormGroup?.disable();
            }
            if (currentTreatmentPrescription.type.value === PrescriptionType.SUPPLY_FARM_ANIMAL_TREATMENT) {
              currentTreatmentPrescription.dosagesFormGroup?.disable();
              currentTreatmentPrescription = {
                ...currentTreatmentPrescription, deliveryOnStockDosageFormGroup: this.fb.group({
                  treatingAnimalsNumber: [existingTreatmentPrescription?.animalCount || '', Validators.required],
                  treatmentDays: [existingTreatmentPrescription?.firstTreatmentDays || '', Validators.required],
                  animalDosis: [existingTreatmentPrescription?.firstDosis || '', Validators.required],
                  units: [{ value: treatmentDefaultData?.salesArticle?.hexUnitCode || '', disabled: true }],
                }, {
                  validators: DeliveryOnStockTotalQuantityValidator(
                    'treatingAnimalsNumber',
                    'treatmentDays',
                    'animalDosis',
                    ratioTreatmentValue,
                  )
                })
              };
              currentTreatmentPrescription.hexTotalDosis = PrescriptionHelper.calcTotalDeliveryQuantity(existingTreatmentPrescription?.animalCount, existingTreatmentPrescription?.firstTreatmentDays, existingTreatmentPrescription?.firstDosis) || 0;
            }
            if (existingTreatmentPrescription && existingTreatmentPrescription.animalTvdNr) {
              this.params.animal.tvd = existingTreatmentPrescription.animalTvdNr;
            }
            if (isabvStateTreatment === PrescriptionStatus.SENT && treatment.isTransferISABV) {
              currentTreatmentPrescription.isIsabv.setValue(true);
              currentTreatmentPrescription.isIsabv?.disable();
            }

            if (tapsStateTreatment === PrescriptionStatus.SENT && treatment.isTransferTAPS) {
              currentTreatmentPrescription.isTaps.setValue(true);
              currentTreatmentPrescription.isTaps?.disable();
            }

            const isTapsDisabled = tapsStateTreatment === PrescriptionStatus.SENT || tapsStateTreatment === PrescriptionStatus.CANCELED || tapsStateTreatment === PrescriptionStatus.RESENT;
            const isIsabvDisabled = isabvStateTreatment === PrescriptionStatus.SENT || isabvStateTreatment === PrescriptionStatus.CANCELED || isabvStateTreatment === PrescriptionStatus.RESENT;

            // Disable dosage when both states are SENT
            if (isTapsDisabled || isIsabvDisabled) {
              this.disableDosageForm(currentTreatmentPrescription);
              currentTreatmentPrescription.type.disable();
            }
            // Disable dosage when isabvState is SENT and taps is not checked
            if (isIsabvDisabled && currentTreatmentPrescription?.isTaps?.value === false) {
              this.disableDosageForm(currentTreatmentPrescription);
            }
            // Disable dosage when tapsState is SENT and isabv is not checked
            if (isTapsDisabled && currentTreatmentPrescription?.isIsabv?.value === false) {
              this.disableDosageForm(currentTreatmentPrescription);
            }
            // Disable dosage when either state is SENT and its openned from report page
            if (this.params.openFromPrescriptionReport && (isIsabvDisabled || isTapsDisabled)) {
              this.disableDosageForm(currentTreatmentPrescription);
              currentTreatmentPrescription.type.disable();
              this.disableGeneralFormData();
            }

            this.treatmentPrescriptions.push(currentTreatmentPrescription);
          });
        this.formGroup.get('hexDiagnosis')?.markAsPristine();
      });

      this.animalSpeciesList = this.animalSpeciesList.filter(specie => specie.codeLevel3 !== undefined);
    }
  }

  private convertTreatmentInstructionsToHexUnits(treatmentInstructions: TreatmentInstruction | undefined, salesArticleId: string, articleId: string, serviceArticleTreatmentsByArticleId: RgTextList[]): TreatmentInstruction | undefined {
    if (treatmentInstructions) {
      return {
        ...treatmentInstructions,
        firstDosage: treatmentInstructions.firstDosage ? MedicalHistoryHelper.convertHexQuantity(treatmentInstructions.firstDosage, salesArticleId, articleId, serviceArticleTreatmentsByArticleId) : treatmentInstructions.firstDosage,
        secondDosage: treatmentInstructions.secondDosage ? MedicalHistoryHelper.convertHexQuantity(treatmentInstructions.secondDosage, salesArticleId, articleId, serviceArticleTreatmentsByArticleId) : treatmentInstructions.secondDosage,
      }
    }
    return treatmentInstructions;
  }

  private disableDosageForm(form: PrescriptionHexRow) {
    form.dosagesFormGroup?.disable();
    form.deliveryOnStockDosageFormGroup?.disable();
    form.startDateInstructions.disable();
    form.endDateInstructions.disable();
  }

  /**
   * Validates if the prescriptions is cancelled and sets it has no_prescription
   * @param existingTreatmentPrescription data of the current prescription saved
   * @param status isabv or taps status
   * @returns returns the correct status
   */
  private setState(existingTreatmentPrescription: Prescription | undefined, status: PrescriptionStatus): PrescriptionStatus {
    if (existingTreatmentPrescription) {
      return this.params.handledCancelledAsNewPrecription && (existingTreatmentPrescription.isabvState === PrescriptionStatus.CANCELED || existingTreatmentPrescription.tapsState === PrescriptionStatus.CANCELED) ? PrescriptionStatus.NO_PRESCRIPTION : status;
    } else {
      return PrescriptionStatus.NO_PRESCRIPTION;
    }
  }

  private getServingModeId(savedTreatmentInstructionName: string | undefined): string | undefined {
    let servingModeId = undefined;
    if (savedTreatmentInstructionName && this.servingModes) {
      const selectedServingMode = this.servingModes.find(servingMode => servingMode.name === savedTreatmentInstructionName);
      if (selectedServingMode) {
        servingModeId = selectedServingMode.id
      }
    }
    return servingModeId
  }
  private getAdministrationId(savedTreatmentInstructionCode: string | undefined): string | undefined {
    let servingModeId = undefined;
    if (savedTreatmentInstructionCode && this.servingModes) {
      const selectedServingMode = this.applicationTypes.find(applicationType => applicationType.code === savedTreatmentInstructionCode);
      if (selectedServingMode) {
        servingModeId = selectedServingMode.id
      }
    }
    return servingModeId
  }

  private loadAnimalSpecies(): void {

    this.store$
      .pipe(
        select(fromStaticDataSelectors.getHexSpecies()),
        untilComponentDestroyed(this)
      )
      .subscribe((species: Species[] | undefined) => {
        this.animalSpeciesList = this.sortSpeciesDiagnosis(species);
      });
  }

  /**
   * Setting the selection of the checkbox in the treatment, it is based on isTaps or TreatmentPrescriptionStatus if exists
   * @param hexSelection
   * @param treatmentPrescriptionStatus
   * @returns
   */
  private getHexSelection(hexSelection: boolean, treatmentPrescriptionStatus: string | undefined): boolean {
    let isSelected = hexSelection;
    if (!treatmentPrescriptionStatus) {
      isSelected = hexSelection || false;
    } else if (treatmentPrescriptionStatus && (treatmentPrescriptionStatus === PrescriptionStatus.SAVED || treatmentPrescriptionStatus === PrescriptionStatus.SENT)) {
      isSelected = true;
    }
    return isSelected
  }

  private sortSpeciesDiagnosis(species?: Species[]): Species[] {
    if (!species) {
      return [];
    }
    const mutableSpeciesDiagnosis = [...species.filter(specie => specie.codeLevel1 === 2 && specie.codeLevel2 === 1)];

    if (mutableSpeciesDiagnosis && mutableSpeciesDiagnosis.length > 0) {
      mutableSpeciesDiagnosis.sort((a, b) => {
        if (this.currentLanguage === this.Languages.de) {
          if (a.designationDe && b.designationDe) {
            return a.designationDe.localeCompare(b.designationDe)
          }
        }
        if (this.currentLanguage === this.Languages.fr) {
          if (a.designationFr && b.designationFr) {
            return a.designationFr.localeCompare(b.designationFr)

          }
        }
        if (this.currentLanguage === this.Languages.it) {
          if (a.designationIt && b.designationIt) {
            return a.designationIt.localeCompare(b.designationIt)
          }
        }
        return 0;
      });
    }
    return mutableSpeciesDiagnosis
  }

  /**
   * Gets selected date and updated the form
   * @param selectedDate Date selected on datepicker
   */
  public dateSelected(selectedDate: Date) {
    this.datePrescription.patchValue(selectedDate);
    this.datePrescription.updateValueAndValidity({ onlySelf: true });
    this.currentPage = PrescriptionDataPages.prescriptionData;
    this.datePrescription.updateValueAndValidity();
    this.formGroup.markAsDirty();
  }

  /**
   * Cancel button action
   * Shows confirmation message based on if form is dirty
   */
  public onNoClick(): void {
    if (this.formGroup.dirty) {

      this.showConfirmation = true;
      this.helperService.scrollTo('confirmationUnsavedChanges', this.changeDetector);

    } else {
      this.dialogRef.close(false);
    }
  }

  public handleTypeChange(): void {
    this.isOnlyDeliveryOnStock = true;
    this.hexDiagnosisControl.value.map((diagnosis: any) => {
      if (diagnosis.treatmentsList && diagnosis.treatmentsList.length > 0) {
        diagnosis.treatmentsList.map((treatmentPrescription: PrescriptionHexRow) => {
          this.isOnlyDeliveryOnStock = (this.isOnlyDeliveryOnStock && (treatmentPrescription.type.value === PrescriptionType.SUPPLY_FARM_ANIMAL_TREATMENT ? true : false));
        })
      }
    });
  }

  /**
   * Used to make save button enable or disabled
   * @returns true or false if the form is valid
   */
  public checkFormValidation(isSentPrescription: boolean): boolean {
    let isInvalid = false;

    if (this.formGroup.invalid) {
      return true;
    }

    if (this.hexDiagnosisControl.invalid) {
      return true;
    }

    // checking is dosis quantity with treatment quantity

    this.hexDiagnosisControl.value.map((diagnosis: any) => {
      if (diagnosis.treatmentsList && diagnosis.treatmentsList.length > 0) {
        diagnosis.treatmentsList.map((treatmentPrescription: PrescriptionHexRow) => {
          if (treatmentPrescription.isabvState === PrescriptionStatus.SENT && treatmentPrescription.tapsState === PrescriptionStatus.SENT) {
            isInvalid = true;
            return;
          } else if (isInvalid === false) {
            const isIsabvSent = treatmentPrescription.isabvState === PrescriptionStatus.SENT;
            const isTapsSent = treatmentPrescription.tapsState === PrescriptionStatus.SENT;
            if (isSentPrescription) {
              // Disabling the sent button based on the checkbox values and prescription state
              // if there is prescription with the state sent the other prescription must be checked
              // If other states are diffrent from sent, it just needs for either checkbox to be checked

              const isIsabvChecked = treatmentPrescription.isIsabv.value === true;
              const isTapsChecked = treatmentPrescription.isTaps.value === true;
              const hasValidChecked =
                (!isIsabvSent && isIsabvChecked) ||
                (!isTapsSent && isTapsChecked);

              isInvalid = !hasValidChecked;
            } else {
              // checking the cases where isTransfer is false,
              if (treatmentPrescription.isTransferISABV === false && (isTapsSent || treatmentPrescription.isTaps.disabled)) {
                isInvalid = true;
              }
              if (treatmentPrescription.isTransferTAPS === false && (isIsabvSent || treatmentPrescription.isIsabv.disabled)) {
                isInvalid = true;
              }
              // Disabling save button when one of the prescriptions is sent
              if (treatmentPrescription.isabvState === PrescriptionStatus.SENT || treatmentPrescription.tapsState === PrescriptionStatus.SENT) {
                isInvalid = true;
              }
              return;
            }
            // Checking if the form of delivery on stock or single farm treatment prescription are valid
            if (treatmentPrescription.type.value === PrescriptionType.SINGLE_FARM_ANIMAL_TREATMENT && treatmentPrescription.dosagesFormGroup && treatmentPrescription.dosagesFormGroup?.enabled && (treatmentPrescription.dosagesFormGroup?.invalid || !treatmentPrescription?.totalDosis || !treatmentPrescription?.totalDeliveryQuantity || treatmentPrescription?.totalDosis > treatmentPrescription?.totalDeliveryQuantity)) {
              isInvalid = true;
            }

            if (treatmentPrescription.type.value === PrescriptionType.SUPPLY_FARM_ANIMAL_TREATMENT && isInvalid === false && treatmentPrescription.deliveryOnStockDosageFormGroup && treatmentPrescription.deliveryOnStockDosageFormGroup.invalid) {
              isInvalid = true
            }
          }

        })
      }
    });
    return isInvalid;
  }

  public showDeleteDeactivateButtonText(): string | undefined {
    const treatments = (this.formGroup.get('hexDiagnosis') as FormArray).controls.flatMap(diagnosis => {
      const treatmentsDiagnosis = diagnosis.get('treatmentsList')?.value || [];
      if (treatmentsDiagnosis && treatmentsDiagnosis.length > 0) {
        return treatmentsDiagnosis;
      }
    });
    if (treatments && treatments.length > 0 && treatments[0]) {
      const treatment = treatments[0];
      if (treatment.tapsState === PrescriptionStatus.SENT || treatment.isabvState === PrescriptionStatus.SENT) {
        return 'reporting.prescription.button.deactivate';
      }
      // check the combinations when state is either saved or one of them is non precribe and the other is saved
      if ((treatment.tapsState === PrescriptionStatus.SAVED && treatment.isabvState === PrescriptionStatus.SAVED) || (treatment.tapsState === PrescriptionStatus.SAVED && treatment.isabvState === PrescriptionStatus.NO_PRESCRIPTION) || (treatment.tapsState === PrescriptionStatus.NO_PRESCRIPTION && treatment.isabvState === PrescriptionStatus.SAVED)) {
        return 'general.label.delete';
      }
    }

    return undefined;
  }

  /**
   * Closes or stays in the model depending on which button user clicked
   * @param e result of confirmation input buttons
   */
  public handleConfirmationPendingChanges(e: boolean): void {
    if (e) {
      this.showConfirmation = false;
      this.dialogRef.close(false);
    } else {
      this.showConfirmation = false;
    }
  }
  /**
   * Checks if the prescription that is been processed is cancelled and its used to not send the oldValues
   * @returns if is resending a cancelled prescription
   */
  private checkIsResendingCancelledPrescriptions(): boolean {
    if (this.params.handledCancelledAsNewPrecription && this.params.currentTreatmentPrescription && this.params.currentTreatmentPrescription.length > 0) {
      const prescription = this.params.currentTreatmentPrescription[0];
      return prescription.isabvState === PrescriptionStatus.CANCELED || prescription.tapsState === PrescriptionStatus.CANCELED;
    }
    return false
  }

  /**
   * Handles the saving of the prescription
   */
  public saveAndClose(shouldSend: boolean): void {
    this.medicalHistoryService.getPrescriptions({ id: this.params.visitId })
      .pipe(untilComponentDestroyed(this))
      .subscribe(oldValuesPrescriptions => {
        const prescriptionParams: Array<PrescriptionWithId> = this.generatePrescriptionParams(shouldSend);
        const isResendingCancelledPrescriptions = this.checkIsResendingCancelledPrescriptions();
        let oldValuesPrescription: Prescription[] = [];
        prescriptionParams.map(prescriptionParam => {
          const existingPrescriptionOldParams = oldValuesPrescriptions.filter(prescription => prescription.prescriptionNumber === prescriptionParam.prescriptionNumber);
          if (existingPrescriptionOldParams && existingPrescriptionOldParams.length > 0) {
            oldValuesPrescription = [...oldValuesPrescription, ...existingPrescriptionOldParams];
          }
        });
        if (oldValuesPrescription && oldValuesPrescription.length > 0 && !isResendingCancelledPrescriptions) {
          const prescriptionSubmittedText: Array<string> = [];
          const prescriptionUnsubmitted: Array<PrescriptionWithId> = [];
          prescriptionParams.map(prescriptionToSave => {
            const isPrescriptionSubmitted = oldValuesPrescription.find(oldPrescription => oldPrescription.treatmentId === prescriptionToSave.treatmentId && oldPrescription.tapsState === PrescriptionStatus.SENT && oldPrescription.isabvState === PrescriptionStatus.SENT);
            if (isPrescriptionSubmitted) {
              const treatmentDescription = this.params.treatments.find(treatment => treatment.id === prescriptionToSave.treatmentId)?.description || ''
              prescriptionSubmittedText.push(treatmentDescription);
            } else {
              prescriptionUnsubmitted.push(prescriptionToSave);
            }
          });
          if (prescriptionSubmittedText.length > 0) {
            this.toastPrescriptionsNotSaved(prescriptionSubmittedText)
          }
          this.savePrescriptionRequest(prescriptionUnsubmitted, oldValuesPrescription, shouldSend);
        } else {
          this.savePrescriptionRequest(prescriptionParams, [], shouldSend);
        }
      });
  }

  public deleteOrDeactivate() {
    var treatmentPrescriptions = (this.formGroup.get('hexDiagnosis') as FormArray).controls.flatMap(diagnosis => {
      const treatmentsDiagnosis = diagnosis.get('treatmentsList')?.value || [];
      if (treatmentsDiagnosis && treatmentsDiagnosis.length > 0) {
        return treatmentsDiagnosis;
      }
    });
    if (treatmentPrescriptions && treatmentPrescriptions.length > 0) {
      var treatmentPrescription: PrescriptionHexRow = treatmentPrescriptions[0];
      const params: DeletePrescription$Params = {
        prescriptionNumber: treatmentPrescription.prescriptionNumber,
        treatmentId: treatmentPrescription.treatmentId,
        isIsabv: (treatmentPrescription.isabvState === PrescriptionStatus.NO_PRESCRIPTION || treatmentPrescription.isabvState === PrescriptionStatus.CANCELED) ? false : treatmentPrescription.isIsabv.value,
        isTaps: (treatmentPrescription.tapsState === PrescriptionStatus.NO_PRESCRIPTION || treatmentPrescription.tapsState === PrescriptionStatus.CANCELED) ? false : treatmentPrescription.isTaps.value
      }
      this.prescriptionService
        .deletePrescription(params)
        .pipe(untilComponentDestroyed(this))
        .subscribe({
          next: async () => {
            this.toastrMsg.showSuccess('general.toastr.success.textContent');
            this.dialogRef.close(true);
          }, error: (error) => {
            this.createErrorHandling(error);
          }
        });
    }
  }

  private toastPrescriptionsNotSaved(prescriptionSubmittedText: Array<string>): void {
    const message = `${this.translateSrv.instant('medicalHistory.modify.hexPrescription.alreadySubmittedPrescriptionMessage1')}  {${prescriptionSubmittedText.toString()}} ${this.translateSrv.instant('medicalHistory.modify.hexPrescription.alreadySubmittedPrescriptionMessage2')}`
    this.toastrMsg.showError(message);
  }

  private generatePrescriptionParams(isSentPrescription: boolean): Array<PrescriptionWithId> {
    const prescriptions: Array<PrescriptionWithId> = [];
    (this.formGroup.get('hexDiagnosis') as FormArray).controls.map(diagnosis => {
      const treatmentsDiagnosis = diagnosis.get('treatmentsList')?.value || [];
      if (treatmentsDiagnosis && treatmentsDiagnosis.length > 0) {
        treatmentsDiagnosis.map((treatmentPrescription: PrescriptionHexRow) => {
          // only send a treatment when isabv or taps checkbox values are true
          if (isSentPrescription) {
            if ((treatmentPrescription.isIsabv.value === true && treatmentPrescription.isIsabv.enabled) || (treatmentPrescription.isTaps.value === true && treatmentPrescription.isTaps.enabled)) {
              const prescription = this.createPrescriptionParam(isSentPrescription, treatmentPrescription, diagnosis);
              prescriptions.push(prescription);
            }
          } else {
            const prescription = this.createPrescriptionParam(isSentPrescription, treatmentPrescription, diagnosis);
            prescriptions.push(prescription);
          }

        });
      }
    });

    return prescriptions;
  }

  private createPrescriptionParam(isSentPrescription: boolean, treatmentPrescription: PrescriptionHexRow, diagnosis: AbstractControl<any, any> | undefined): PrescriptionWithId {
    let diagnosisDescription = undefined;
    let selectedDiagnosis = undefined;
    if (diagnosis) {
      selectedDiagnosis = this.diagnosisList.find(d => d.id == diagnosis.get('diagnosisLevel2')?.value);
      if (selectedDiagnosis) {
        switch (this.currentLanguage) {
          case Languages.de: {
            diagnosisDescription = MedicalHistoryHelper.removeCodeFromPrescriptionText(selectedDiagnosis.designationDe);
            break;
          }
          case Languages.fr: {
            diagnosisDescription = MedicalHistoryHelper.removeCodeFromPrescriptionText(selectedDiagnosis.designationFr);
            break;
          }
          case Languages.it: {
            diagnosisDescription = MedicalHistoryHelper.removeCodeFromPrescriptionText(selectedDiagnosis.designationIt);
            break;
          }
        }
      }
    }

    const weight = this.weightControl.value !== '' && !isNaN(this.weightControl.value) ? Number(this.weightControl.value) : undefined;
    let speciesKey = undefined;
    let tvd = undefined;
    speciesKey = this.animalSpeciesControl.value.codeLevel1 + '.' + this.animalSpeciesControl.value.codeLevel2 + (this.animalSpeciesControl.value.codeLevel3 ? '.' + this.animalSpeciesControl.value.codeLevel3 : '');
    tvd = this.params.animal.tvd ? this.params.animal.tvd : this.animalTvdNumberControl.value?.tvdNumber || undefined;

    let prescription: PrescriptionWithId = {
      treatmentId: treatmentPrescription.treatmentId,
      id: treatmentPrescription.prescriptionNumber,
      treatmentPosQuantity: treatmentPrescription.hexTotalDeliveryQuantity,
      isIsabv: isSentPrescription ? (!treatmentPrescription.isIsabv.disabled ? treatmentPrescription.isIsabv.value : false) : treatmentPrescription.isTransferISABV,
      isTaps: isSentPrescription ? (!treatmentPrescription.isTaps.disabled ? treatmentPrescription.isTaps.value : false) : treatmentPrescription.isTransferTAPS,
      totalDosis: treatmentPrescription.hexTotalDosis,
      treatmentEnd: DateTimeHelper.toTimeString(
        treatmentPrescription.endDateInstructions.value,
        'YYYY-MM-DDTHH:mm:ss'
      ),
      treatmentStart: DateTimeHelper.toTimeString(
        treatmentPrescription.startDateInstructions.value,
        'YYYY-MM-DDTHH:mm:ss'
      ),
      animalWeight: weight,
      animalSpeciesCategoryKey: speciesKey,
      resourceId: this.params.personalResourceId,
      prescriptionNumber: treatmentPrescription.prescriptionNumber,
      tapsState: treatmentPrescription.tapsState,
      isabvState: treatmentPrescription.isabvState,
      diagnosisDescription: diagnosisDescription,
      diagnosisKey: selectedDiagnosis ? selectedDiagnosis.codeLevel1 + '.' + selectedDiagnosis.codeLevel2 : '',
      animalTvdNr: AnimalHelper.formatTvdNumber(tvd),
      totalDeliveryQuantity: treatmentPrescription.hexTotalDeliveryQuantity,
      isCriticalAntibiotic: treatmentPrescription.isCriticalAntibiotic,
      type: treatmentPrescription.type.value,
      animalCount: 1
    };
    if (treatmentPrescription.dosagesFormGroup && treatmentPrescription.type.value === PrescriptionType.SINGLE_FARM_ANIMAL_TREATMENT) {
      prescription = {
        ...prescription,
        ...{
          injectionPointWithdrawalPeriod: treatmentPrescription.type.value === PrescriptionType.SINGLE_FARM_ANIMAL_TREATMENT ? MedicalHistoryHelper.getNumber((treatmentPrescription.dosagesFormGroup.get('injectionPointWithdrawalPeriod') as FormControl).value) : undefined,
          meatWithdrawalPeriod: treatmentPrescription.type.value === PrescriptionType.SINGLE_FARM_ANIMAL_TREATMENT ? MedicalHistoryHelper.getNumber((treatmentPrescription.dosagesFormGroup.get('meatWithdrawalPeriod') as FormControl).value) : undefined,
          organWithdrawalPeriod: treatmentPrescription.type.value === PrescriptionType.SINGLE_FARM_ANIMAL_TREATMENT ? MedicalHistoryHelper.getNumber((treatmentPrescription.dosagesFormGroup.get('organWithdrawalPeriod') as FormControl).value) : undefined,
          milkWithdrawalPeriod: treatmentPrescription.type.value === PrescriptionType.SINGLE_FARM_ANIMAL_TREATMENT ? MedicalHistoryHelper.getNumber((treatmentPrescription.dosagesFormGroup.get('milkWithdrawalPeriod') as FormControl).value) : undefined,
        }
      }

      const dosages = treatmentPrescription.dosagesFormGroup.get('dosages') as FormArray;
      if (dosages.controls.length > 0) {
        prescription.firstDosis = Number(dosages.controls[0].get('amount')?.value);
        prescription.firstTreatmentDays = Number(dosages.controls[0].get('durationInDays')?.value);
        prescription.firstServingMode = dosages.controls[0].get('servingMode')?.value.name;
        prescription.medicineAdministration = dosages.controls[0].get('applicationType')?.value.code;
      };
      if (dosages.controls.length > 1) {
        prescription.secondDosis = Number(dosages.controls[1].get('amount')?.value);
        prescription.secondTreatmentDays = Number(dosages.controls[1].get('durationInDays')?.value);
        prescription.secondServingMode = dosages.controls[1].get('servingMode')?.value.name;
      }
    }

    if (treatmentPrescription.deliveryOnStockDosageFormGroup && treatmentPrescription.type.value === PrescriptionType.SUPPLY_FARM_ANIMAL_TREATMENT) {
      const deliveryOnStockDosage = treatmentPrescription.deliveryOnStockDosageFormGroup as FormGroup;
      prescription = {
        ...prescription,
        animalCount: Number(deliveryOnStockDosage.get('treatingAnimalsNumber')?.value),
        firstDosis: deliveryOnStockDosage.get('animalDosis')?.value ? Number(deliveryOnStockDosage.get('animalDosis')?.value) : undefined,
        firstTreatmentDays: deliveryOnStockDosage.get('treatmentDays')?.value ? Number(deliveryOnStockDosage.get('treatmentDays')?.value) : undefined,
      }
    }
    return prescription
  }

  private correctOldValuesIsTapsIsisabv(oldValues: Prescription[]): PrescriptionWithId[] {
    return oldValues.map(prescription => {
      const prescriptionWithId: any = {
        ...prescription,
        id: prescription.prescriptionNumber
      }
      prescriptionWithId.isIsabv = prescription.isabvState !== PrescriptionStatus.NO_PRESCRIPTION ? true : false;
      prescriptionWithId.isTaps = prescription.tapsState !== PrescriptionStatus.NO_PRESCRIPTION ? true : false;
      prescriptionWithId.medicineAdministration = prescription.medicineAdministration || '';
      prescriptionWithId.diagnosisKey = prescription.diagnosisKey || '';
      prescriptionWithId.meatWithdrawalPeriod = prescription.meatWithdrawalPeriod !== undefined ? prescription.meatWithdrawalPeriod : null;
      prescriptionWithId.organWithdrawalPeriod = prescription.organWithdrawalPeriod !== undefined ? prescription.organWithdrawalPeriod : null;
      prescriptionWithId.milkWithdrawalPeriod = prescription.milkWithdrawalPeriod !== undefined ? prescription.milkWithdrawalPeriod : null;
      prescriptionWithId.injectionPointWithdrawalPeriod = prescription.injectionPointWithdrawalPeriod !== undefined ? prescription.injectionPointWithdrawalPeriod : null;
      return prescriptionWithId;
    });
  }

  private sortByUuid(prescription: PrescriptionWithId[]): PrescriptionWithId[] {
    return prescription.sort((a, b) => {
      if (a.prescriptionNumber < b.prescriptionNumber) {
        return -1;
      }
      if (a.prescriptionNumber > b.prescriptionNumber) {
        return 1;
      }
      return 0;
    });
  }

  private savePrescriptionRequest(prescriptions: Array<PrescriptionWithId>, oldValues: Prescription[], shouldSend: boolean): void {
    this.errorResponse = [];
    const correctedOldValues = this.correctOldValuesIsTapsIsisabv(oldValues);
    const sortNewValues = this.sortByUuid(prescriptions);
    const sortOldValues = this.sortByUuid(correctedOldValues);
    const eventData = this.eventGenerator.mapDataToEvent({
      newValues: [{
        prescriptions: sortNewValues
      }],
      oldValues: [{
        prescriptions: sortOldValues || []
      }],
      isListDiff: true
    });
    // When sending to isabv or taps is required to have values
    if (shouldSend) {
      eventData.newValues[0] = {
        prescriptions: sortNewValues
      }
      eventData.oldValues[0] = {
        prescriptions: sortOldValues || []
      }
    }
    if (eventData.newValues[0].prescriptions && eventData.newValues[0].prescriptions.length > 0) {
      const savePrescriptions: SavePrescriptions$Params = {
        id: this.params.visitId,
        body: {
          newValues: eventData.newValues[0],
          oldValues: eventData.oldValues[0].prescriptions === undefined ? { prescriptions: [] } : eventData.oldValues[0],
          isSend: shouldSend
        }
      }
      this.medicalHistoryService.savePrescriptions(savePrescriptions)
        .pipe(untilComponentDestroyed(this)).subscribe((result) => {
          this.toastrMsg.showSuccess('general.toastr.success.textContent');
          this.dialogRef.close(true);
        }, errorData => {
          this.formGroup.markAsPristine();
          this.updateFormValues(prescriptions[0].prescriptionNumber);
          this.createErrorHandling(errorData);
        });
    } else {
      this.toastrMsg.showWarning('medicalHistory.modify.prescriptionHex.warning.noChanges');
      this.dialogRef.close(true);
    }
  }

  private updateFormValues(prescriptionNumber: string): void {
    this.medicalHistoryService.getPrescriptions({ id: this.params.visitId })
      .pipe(untilComponentDestroyed(this))
      .subscribe(oldValuesPrescription => {
        this.params.currentTreatmentPrescription = oldValuesPrescription.filter(prescription => prescription.prescriptionNumber === prescriptionNumber);
        this.initializePrescriptionTreatment();
      });
  }

  private createErrorHandling(errorData: any): void {
    this.errorResponse = [];
    const errorDetails: Array<ValidationError> = ErrorMsgHelper.parseError(errorData);
    if (errorDetails.length > 0) {
      const uniqueTreatmentIds = Array.from(new Set(errorDetails.map(error => error.attribute)));

      uniqueTreatmentIds.map(treatmentId => {
        const treatmentWithErrors = this.treatmentPrescriptions.find(treatmentPrescription => treatmentPrescription.treatmentId === treatmentId);
        const errorByTreatment = errorDetails.filter(error => error.attribute === treatmentId);
        let messages: Array<TranslationError> = [];

        if (errorByTreatment && errorByTreatment.length > 0) {
          errorByTreatment.map(errorTreatment => {
            this.processError(errorTreatment, messages);
          });
        }

        const errorHex: ErrorHex = {
          // remove duplicated errors
          messages: Array.from(new Set(messages)),
          treatmentName: treatmentWithErrors ? treatmentWithErrors.medicineDescription : ''
        };
        this.errorResponse.push(errorHex);
      });

      this.helperService.scrollTo('errorsArea', this.changeDetector);
    }
  }

  private processError(errorTreatment: ValidationError, messages: Array<TranslationError>) {
    let prescriptionSource: PrescriptionSource = PrescriptionSource.UNKNOWN;
    if (errorTreatment.params && errorTreatment.params.includes("ISABV_ERROR")) {
      prescriptionSource = PrescriptionSource.ISABV;
    } else if (errorTreatment.params && errorTreatment.params.includes("TAPS_ERROR")) {
      prescriptionSource = PrescriptionSource.TAPS;
    }

    let translationError: TranslationError = { source: prescriptionSource };
    let translatedKey = this.translationService.instant(errorTreatment.message || '');
    if (translatedKey && translatedKey !== errorTreatment.message) {
      translationError.key = errorTreatment.message;
    }

    if (errorTreatment.params && errorTreatment.params.length > 0) {
      // Converting response params response from ["de: text", "it: text", "fr: text"] to [{de:"text", it: "text", fr: "text"}]
      errorTreatment.params.map(param => {
        const splitByDots = param.split(':');
        if (splitByDots.length > 1) {
          switch (splitByDots[0]) {
            case 'tapsErrorMsg': { translationError.tapsErrorMsg = splitByDots[1].trim(); break; }
            case 'de': { translationError.de = splitByDots[1].trim(); break; }
            case 'fr': { translationError.fr = splitByDots[1].trim(); break; }
            case 'it': { translationError.it = splitByDots[1].trim(); break; }
          }
        }
      });
    }
    messages.push(translationError);
  }

  public showPrescription(): void {
    this.showPrescriptionValidation = true;
    this.loadDiagnosisSpecies();
  }

  public showCalendarPage(date: any): void {
    if (date) {
      this.datePrescription = date;
      this.currentPage = this.pages.calendarPicker;
    }
  }
}
